import { useState, useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { useBrowsePlans } from '@/hooks/usePlanBrowse'
import { useDeletePlan, useCopyPlan } from '@/hooks/useAssortmentPlans'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  EditablePlanNameCell,
  ActiveVersionCell,
  TextCell,
  ActionsCell,
  StatusBadgeCell,
} from '@/components/AssortmentPlansGrid/cellRenderers'
import type { AssortmentPlan } from '@/types'
import type { BrowsePlansParams } from '@/api/planBrowse'

ModuleRegistry.registerModules([AllCommunityModule])

interface BrowsePlansGridProps {
  filters: Omit<BrowsePlansParams, 'page' | 'pageSize'>
}

export function BrowsePlansGrid({ filters }: BrowsePlansGridProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.BROWSE_PLANS_GRID,
    20
  )

  const queryFilters: BrowsePlansParams = useMemo(
    () => ({ ...filters, page, pageSize }),
    [filters, page, pageSize]
  )

  const { data, isLoading } = useBrowsePlans(queryFilters)

  const plans = data?.plans ?? []
  const totalPages = data?.totalPages ?? 1

  // Copy / Delete state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<AssortmentPlan | null>(null)

  const deleteMutation = useDeletePlan()
  const copyMutation = useCopyPlan()

  const handleCopyClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setCopyDialogOpen(true)
  }, [])

  const handleDeleteClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setDeleteDialogOpen(true)
  }, [])

  const handleConfirmCopy = useCallback(() => {
    if (selectedPlan && selectedPlan.active_version_id) {
      copyMutation.mutate(
        {
          name: `${selectedPlan.name} (Copy)`,
          sourceVersionId: selectedPlan.active_version_id,
          hierarchyId: selectedPlan.hierarchy_id,
        },
        {
          onSettled: () => {
            setCopyDialogOpen(false)
            setSelectedPlan(null)
          },
        }
      )
    }
  }, [selectedPlan, copyMutation])

  const handleConfirmDelete = useCallback(() => {
    if (selectedPlan) {
      deleteMutation.mutate(selectedPlan.id, {
        onSettled: () => {
          setDeleteDialogOpen(false)
          setSelectedPlan(null)
        },
      })
    }
  }, [selectedPlan, deleteMutation])

  const columnDefs: ColDef<AssortmentPlan>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 180,
        cellRenderer: EditablePlanNameCell,
      },
      {
        field: 'hierarchy',
        headerName: 'Hierarchy',
        flex: 3,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'versions_count',
        headerName: 'Version',
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        cellRenderer: ActiveVersionCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 100,
        cellRenderer: TextCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: StatusBadgeCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 90,
        maxWidth: 120,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onCopy: handleCopyClick,
          onDelete: handleDeleteClick,
        },
      },
    ],
    [handleCopyClick, handleDeleteClick]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <>
      <ConfirmationDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        title="Copy plan"
        description={
          selectedPlan?.active_version_id
            ? `Are you sure you want to copy "${selectedPlan?.name ?? ''}"? A duplicate will be created.`
            : `Cannot copy "${selectedPlan?.name ?? ''}" because it has no active version.`
        }
        confirmText="Copy"
        cancelText="Cancel"
        onConfirm={handleConfirmCopy}
        isLoading={copyMutation.isPending}
        confirmDisabled={!selectedPlan?.active_version_id}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete plan"
        description={`Are you sure you want to delete "${selectedPlan?.name ?? ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <div className="w-full">
        <div className="border border-[#e7e7e7] bg-white overflow-hidden">
          <div className="ag-theme-michaels w-full">
            <AgGridReact<AssortmentPlan>
              rowData={plans}
              columnDefs={columnDefs}
              theme="legacy"
              loading={isLoading}
              domLayout="autoHeight"
              rowHeight={68}
              headerHeight={48}
              autoSizeStrategy={{ type: 'fitGridWidth' }}
              onGridSizeChanged={onGridSizeChanged}
              overlayLoadingTemplate="<span>Loading...</span>"
              overlayNoRowsTemplate="<span>No plans found</span>"
              suppressCellFocus={true}
              suppressRowHoverHighlight={false}
              suppressColumnVirtualisation={true}
              suppressMovableColumns={true}
            />
          </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>
    </>
  )
}
